import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import ReactQuillSimple from "./ReactQuill";
import { uploadImageToCloudflare } from "./model/uploadImageToCloudflare";
import type { QuillSelectKeyHandler } from "./types";

interface EditorLikeRef {
  editor: {
    getContent: () => string;
  };
}

interface EditorAdapterProps {
  value?: string | null;
  defaultValue?: string | null;
  onChange?: (value: string) => void;
  disabled?: boolean;
  autoGrow?: boolean;
  /** When set, shows toolbar button to open keys directory and insert at cursor. */
  onSelectKey?: QuillSelectKeyHandler;
}

const EditorAdapter = forwardRef<EditorLikeRef, EditorAdapterProps>(
  ({ value = null, defaultValue, onChange, disabled = false, autoGrow = false, onSelectKey }, ref) => {
    const [innerValue, setInnerValue] = useState<string>(defaultValue || "");

    const editorValue = useMemo(() => {
      if (value !== null && value !== undefined) return value;
      return innerValue;
    }, [innerValue, value]);

    const handleChange = (newValue: string) => {
      if (typeof onChange === "function") onChange(newValue);
      else setInnerValue(newValue);
    };

    useImperativeHandle(
      ref,
      () => ({
        editor: {
          getContent: () => editorValue || "",
        },
      }),
      [editorValue]
    );

    return (
      <ReactQuillSimple
        value={editorValue || ""}
        onChange={handleChange}
        disabled={disabled}
        autoGrow={autoGrow}
        uploadImageFunction={uploadImageToCloudflare}
        uploadPastedImage={uploadImageToCloudflare}
        onSelectKey={onSelectKey}
      />
    );
  }
);

EditorAdapter.displayName = "EditorAdapter";

export default EditorAdapter;
export type { QuillSelectKeyHandler };
